/**
 * Connection Manager for handling browser connection pool limits
 *
 * Browsers typically allow 6-8 concurrent HTTP/2 connections per domain.
 * This manager queues requests when the limit is reached and prioritizes
 * critical application requests over analytics/tracking.
 */

export interface RequestPriority {
  HIGH: 'high'
  NORMAL: 'normal'
  LOW: 'low'
}

export const REQUEST_PRIORITY: RequestPriority = {
  HIGH: 'high',
  NORMAL: 'normal',
  LOW: 'low'
}

interface QueuedRequest {
  requestFn: () => Promise<any>
  priority: keyof RequestPriority
  resolve: (value: any) => void
  reject: (error: any) => void
  timestamp: number
}

class ConnectionManager {
  private activeRequests = 0
  private readonly maxConcurrent: number
  private highPriorityQueue: QueuedRequest[] = []
  private normalPriorityQueue: QueuedRequest[] = []
  private lowPriorityQueue: QueuedRequest[] = []

  constructor(maxConcurrent = 6) {
    this.maxConcurrent = maxConcurrent
  }

  /**
   * Execute a request with priority and queue management
   */
  async execute<T>(
    requestFn: () => Promise<T>,
    priority: keyof RequestPriority = 'NORMAL'
  ): Promise<T> {
    if (this.activeRequests < this.maxConcurrent) {
      return this.executeNow(requestFn)
    }

    // Queue the request by priority
    return new Promise<T>((resolve, reject) => {
      const queuedRequest: QueuedRequest = {
        requestFn,
        priority,
        resolve,
        reject,
        timestamp: Date.now()
      }

      this.addToQueue(queuedRequest)
    })
  }

  /**
   * Get priority for URL - critical app requests get high priority
   */
  getRequestPriority(url: string): keyof RequestPriority {
    // High priority: chat, task submission, critical API calls
    if (
      url.includes('/chat') ||
      url.includes('/task-submit') ||
      url.includes('/conversation/') ||
      url.includes('/user/') ||
      url.includes('/org/')
    ) {
      return 'HIGH'
    }

    // Low priority: analytics, tracking, non-critical requests
    if (
      url.includes('clarity') ||
      url.includes('analytics') ||
      url.includes('collect') ||
      url.includes('trace')
    ) {
      return 'LOW'
    }

    // Normal priority: everything else
    return 'NORMAL'
  }

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      active: this.activeRequests,
      maxConcurrent: this.maxConcurrent,
      queued: {
        high: this.highPriorityQueue.length,
        normal: this.normalPriorityQueue.length,
        low: this.lowPriorityQueue.length
      }
    }
  }

  private async executeNow<T>(requestFn: () => Promise<T>): Promise<T> {
    this.activeRequests++

    try {
      const result = await requestFn()
      return result
    } finally {
      this.activeRequests--
      this.processQueue()
    }
  }

  private addToQueue(request: QueuedRequest) {
    switch (request.priority) {
      case 'HIGH':
        this.highPriorityQueue.push(request)
        break
      case 'NORMAL':
        this.normalPriorityQueue.push(request)
        break
      case 'LOW':
        this.lowPriorityQueue.push(request)
        break
    }
  }

  private processQueue() {
    if (this.activeRequests >= this.maxConcurrent) {
      return
    }

    // Process high priority first, then normal, then low
    const nextRequest =
      this.highPriorityQueue.shift() ||
      this.normalPriorityQueue.shift() ||
      this.lowPriorityQueue.shift()

    if (nextRequest) {
      this.executeNow(nextRequest.requestFn).then(nextRequest.resolve).catch(nextRequest.reject)
    }
  }

  /**
   * Clear old queued requests (prevent memory leaks)
   */
  private cleanup() {
    const maxAge = 5 * 60 * 1000 // 5 minutes
    const now = Date.now()

    const cleanQueue = (queue: QueuedRequest[]) => {
      return queue.filter(request => {
        if (now - request.timestamp > maxAge) {
          request.reject(new Error('Request timed out in queue'))
          return false
        }
        return true
      })
    }

    this.highPriorityQueue = cleanQueue(this.highPriorityQueue)
    this.normalPriorityQueue = cleanQueue(this.normalPriorityQueue)
    this.lowPriorityQueue = cleanQueue(this.lowPriorityQueue)
  }

  // Run cleanup every 2 minutes
  startCleanup() {
    setInterval(() => this.cleanup(), 2 * 60 * 1000)
  }
}

// Create singleton instance
export const connectionManager = new ConnectionManager()

// Start cleanup process
connectionManager.startCleanup()

// Expose status for debugging
if (typeof window !== 'undefined') {
  ;(window as any).connectionManager = connectionManager
}
